<!-- Loading state -->
@if (detailLoading$ | async) {
  <div
    class="flex justify-content-center align-items-center"
    style="height: 60vh"
  >
    <p-progressSpinner
      strokeWidth="4"
      [style]="{ width: '48px', height: '48px' }"
    ></p-progressSpinner>
    <span class="ml-3 text-color-secondary text-lg">Chargement du run…</span>
  </div>
}

@if (run(); as detail) {
  <div class="run-detail-layout">
    <!-- ===== EN-TÊTE ===== -->
    <div class="detail-header">
      <div class="flex align-items-center gap-2">
        <p-button
          icon="pi pi-arrow-left"
          [text]="true"
          severity="secondary"
          (onClick)="goBack()"
          pTooltip="Retour à la liste"
          tooltipPosition="right"
        ></p-button>
        <h2 class="m-0">Run #{{ detail.id }}</h2>
        <p-tag
          [value]="getStatusLabel(detail.status)"
          [severity]="getStatusSeverity(detail.status)"
        ></p-tag>
      </div>

      <!-- Méta-données compactes -->
      <div class="meta-chips">
        @if (detail.dateFrom && detail.dateTo) {
          <span class="meta-chip">
            <i class="pi pi-calendar mr-1"></i>
            {{ detail.dateFrom | date: 'dd/MM/yy' }} →
            {{ detail.dateTo | date: 'dd/MM/yy' }}
          </span>
        }
        @if (detail.serviceDuration) {
          <span class="meta-chip">
            <i class="pi pi-clock mr-1"></i>
            Durée service : {{ formatServiceDuration(detail.serviceDuration) }}
          </span>
        }
        @if (detail.maxRadiusKm) {
          <span class="meta-chip">
            <i class="pi pi-map-marker mr-1"></i>
            Rayon : {{ detail.maxRadiusKm }} km
          </span>
        }
        @if (detail.techniciansCount) {
          <span class="meta-chip">
            <i class="pi pi-users mr-1"></i>
            {{ detail.techniciansCount }} technicien(s)
          </span>
        }
        @if (detail.clustersCount) {
          <span class="meta-chip">
            <i class="pi pi-sitemap mr-1"></i>
            {{ detail.clustersCount }} cluster(s)
          </span>
        }
        @if (coveragePercent(); as pct) {
          <span
            class="meta-chip font-semibold"
            [class.text-green-600]="pct >= 80"
            [class.text-orange-600]="pct >= 50 && pct < 80"
            [class.text-red-600]="pct < 50"
          >
            <i class="pi pi-chart-pie mr-1"></i>
            Couverture {{ pct }}% ({{ detail.affairsWithSlotsCount }}/{{
              detail.affairsCount
            }})
          </span>
        }
      </div>
    </div>

    @if (detail.errorMessage) {
      <div class="error-block mb-3">
        <i class="pi pi-exclamation-triangle mr-2 text-red-500"></i>
        <span class="text-red-700">{{ detail.errorMessage }}</span>
      </div>
    }

    <!-- ===== BARRE DE RECHERCHE ===== -->
    <div class="search-bar">
      <p-iconfield iconPosition="left" class="search-field">
        <p-inputicon styleClass="pi pi-search"></p-inputicon>
        <input
          pInputText
          type="text"
          [(ngModel)]="searchQuery"
          placeholder="Rechercher par nom, code postal, localité, groupe…"
          class="w-full"
        />
      </p-iconfield>
      @if (searchQuery) {
        <p-button
          icon="pi pi-times"
          [text]="true"
          severity="secondary"
          size="small"
          (onClick)="clearSearch()"
          pTooltip="Effacer la recherche"
        ></p-button>
      }
      <span class="results-count text-color-secondary text-sm">
        {{ filteredAffairs.length }} affaire(s) ·
        {{ totalSlotsCount() }} créneau(x) au total
      </span>
      @if (detail.poolAffairsWithoutSlots?.length) {
        <p-tag
          [value]="detail.poolAffairsWithoutSlots!.length + ' sans créneau'"
          severity="warn"
        ></p-tag>
      }
    </div>

    <!-- ===== PROPOSITIONS AVEC CHANGEMENT DE JOUR ===== -->
    @if (dayShiftProposals.length > 0) {
      <div class="day-shift-section">
        <h3 class="day-shift-title">
          <i class="pi pi-calendar-plus"></i>
          Propositions avec changement de jour ({{ dayShiftProposals.length }})
        </h3>
        <p class="day-shift-hint">
          Créneaux dont la tournée déplace des interventions déjà programmées
          sur un autre jour de leur période autorisée (passe globale).
        </p>
        <div class="day-shift-grid">
          @for (proposal of dayShiftProposals; track proposal.slotId) {
            <button
              type="button"
              class="day-shift-item"
              (click)="goToProposalAffair(proposal)"
              pTooltip="Voir l'affaire et le tour_data du créneau"
              tooltipPosition="top"
            >
              <div class="day-shift-item-head">
                <span class="day-shift-item-name">
                  {{ proposal.affairName }}
                </span>
                <span class="day-shift-item-rank">rang {{ proposal.rank }}</span>
              </div>
              <div class="day-shift-item-meta">
                <span>
                  <i class="pi pi-calendar"></i>
                  {{ proposal.date | date: 'EEE dd/MM/yyyy' : '' : 'fr-FR' }}
                </span>
                @if (proposal.technicianName) {
                  <span>
                    <i class="pi pi-user"></i>
                    {{ proposal.technicianName }}
                  </span>
                }
              </div>
              @for (move of proposal.moves; track $index) {
                <div class="day-shift-item-move">
                  <span class="move-name">{{ move.name }}</span>
                  @if (move.originalDate && move.newDate) {
                    <span class="move-dates">
                      {{ move.originalDate | date: 'dd/MM' }}
                      <i class="pi pi-arrow-right"></i>
                      {{ move.newDate | date: 'dd/MM' }}
                      @if (move.newStart) {
                        à {{ move.newStart }}
                      }
                    </span>
                  }
                  @if (move.postponablePeriod) {
                    <span class="move-period">
                      {{ postponableLabel(move.postponablePeriod) }}
                    </span>
                  }
                </div>
              }
            </button>
          }
        </div>
      </div>
    }

    <!-- ===== LISTE DES AFFAIRES ===== -->
    @if (filteredAffairs.length === 0 && searchQuery) {
      <div class="no-results">
        <i class="pi pi-search text-2xl text-color-secondary"></i>
        <span class="text-color-secondary"
          >Aucune affaire ne correspond à "{{ searchQuery }}"</span
        >
      </div>
    }

    <div class="affairs-list">
      @for (affair of filteredAffairs; track affair.affairId) {
        <div
          class="affair-row"
          [attr.id]="'affair-row-' + affair.affairId"
          [class.affair-row--expanded]="expandedAffairId === affair.affairId"
        >
          <!-- En-tête affaire -->
          <div class="affair-header" (click)="toggleAffair(affair.affairId)">
            <div class="flex align-items-center gap-2 min-w-0">
              <span class="rank-badge">#{{ affair.efficiencyRank }}</span>
              <div class="min-w-0">
                <div class="font-semibold text-sm affair-name">
                  {{ affair.affairName }}
                  <span class="text-xs text-color-secondary font-normal ml-1"
                    >(ID {{ affair.affairId }})</span
                  >
                </div>
                <div class="text-xs text-color-secondary">
                  {{ affair.postalCode }} {{ affair.locality }}
                  @if (affair.groupingName) {
                    ·
                    <span class="text-primary font-medium">{{
                      affair.groupingName
                    }}</span>
                  }
                </div>
                @if (affair.affairProducts?.length) {
                  <div class="affair-products mt-1">
                    @for (ap of affair.affairProducts!; track $index) {
                      <span class="product-chip">
                        {{
                          ap['product_name'] ??
                            ap['product']?.['abbreviation'] ??
                            ap['product']?.['name'] ??
                            'Produit #' + ap['product_id']
                        }}
                      </span>
                    }
                  </div>
                }
              </div>
            </div>

            <div class="flex align-items-center gap-3 flex-shrink-0">
              @if (affair.source) {
                <p-tag
                  [value]="affair.source"
                  severity="info"
                  [style]="{ fontSize: '0.7rem' }"
                ></p-tag>
              }
              <span class="text-sm text-color-secondary">
                <i class="pi pi-calendar mr-1"></i>
                {{ affair.slots.length }} créneau(x)
              </span>
              @if (affair.bestTourDurationMin !== null) {
                <span class="text-sm">
                  <i class="pi pi-clock mr-1 text-color-secondary"></i>
                  <span class="font-semibold">{{
                    affair.bestTourDurationMin | number: '1.0-0'
                  }}</span>
                  <span class="text-xs text-color-secondary">
                    min (meilleur)</span
                  >
                </span>
              }
              @if (affair.averageTourDurationMin !== null) {
                <span class="text-sm text-color-secondary">
                  moy. {{ affair.averageTourDurationMin | number: '1.0-0' }} min
                </span>
              }
              <i
                class="pi"
                [class.pi-chevron-down]="expandedAffairId !== affair.affairId"
                [class.pi-chevron-up]="expandedAffairId === affair.affairId"
              ></i>
            </div>
          </div>

          <!-- Tableau des créneaux -->
          @if (expandedAffairId === affair.affairId) {
            <div class="slots-detail">
              <table class="slots-table">
                <thead>
                  <tr>
                    <th>Rang</th>
                    <th>Date</th>
                    <th>Heure arrivée</th>
                    <th>Technicien</th>
                    <th>Email</th>
                    <th>Trajet (min)</th>
                    <th>Service (min)</th>
                    <th>Attente (min)</th>
                    <th>Distance (km)</th>
                    <th>Durée tournée (min)</th>
                    <th>Dist. tournée (km)</th>
                    <th>Arrêts</th>
                    <th>tour_data</th>
                  </tr>
                </thead>
                <tbody>
                  @for (slot of affair.slots; track slot.id) {
                    <tr [class.slot-best]="slot.rank === 1">
                      <td>
                        <span class="font-mono text-sm">{{ slot.rank }}</span>
                        @if (slot.rank === 1) {
                          <i
                            class="pi pi-star-fill text-yellow-500 ml-1 text-xs"
                            pTooltip="Meilleur créneau"
                          ></i>
                        }
                      </td>
                      <td class="font-semibold">
                        {{ slot.date | date: 'EEE dd/MM/yyyy' : '' : 'fr-FR' }}
                      </td>
                      <td class="font-mono text-sm">
                        {{ slot.arrivalTime ?? '—' }}
                      </td>
                      <td>{{ slot.technicianName ?? '—' }}</td>
                      <td class="text-xs text-color-secondary">
                        {{ slot.technicianEmail ?? '—' }}
                      </td>
                      <td class="text-center">
                        {{ slot.travelDurationMin | number: '1.1-1' }}
                      </td>
                      <td class="text-center">
                        {{ slot.serviceDurationMin | number: '1.1-1' }}
                      </td>
                      <td class="text-center">
                        {{ slot.waitingMin | number: '1.1-1' }}
                      </td>
                      <td class="text-center">
                        {{
                          slot.distanceKm !== null
                            ? (slot.distanceKm | number: '1.1-1')
                            : '—'
                        }}
                      </td>
                      <td class="text-center font-semibold">
                        {{
                          slot.tourTotalDurationMin !== null
                            ? (slot.tourTotalDurationMin | number: '1.0-0')
                            : '—'
                        }}
                      </td>
                      <td class="text-center">
                        {{
                          slot.tourTotalDistanceKm !== null
                            ? (slot.tourTotalDistanceKm | number: '1.1-1')
                            : '—'
                        }}
                      </td>
                      <td class="text-center">
                        {{ slot.tourStopsCount ?? '—' }}
                      </td>
                      <td class="text-center">
                        @if (slot.tourData) {
                          <button
                            type="button"
                            class="tour-data-toggle"
                            (click)="
                              toggleTourData(slot.id); $event.stopPropagation()
                            "
                            [title]="
                              expandedTourDataSlotIds.has(slot.id)
                                ? 'Masquer tour_data'
                                : 'Afficher tour_data'
                            "
                          >
                            <i
                              class="pi"
                              [class.pi-code]="
                                !expandedTourDataSlotIds.has(slot.id)
                              "
                              [class.pi-times]="
                                expandedTourDataSlotIds.has(slot.id)
                              "
                            ></i>
                          </button>
                        } @else {
                          <span class="text-color-secondary">—</span>
                        }
                      </td>
                    </tr>
                    @if (
                      expandedTourDataSlotIds.has(slot.id) && slot.tourData
                    ) {
                      <tr class="tour-data-row">
                        <td colspan="13">
                          <pre class="tour-data-pre">{{
                            formatTourData(slot.tourData)
                          }}</pre>
                        </td>
                      </tr>
                    }
                  }
                </tbody>
              </table>
            </div>
          }
        </div>
      }
    </div>

    <!-- ===== AFFAIRES SANS CRÉNEAUX ===== -->
    @if (detail.poolAffairsWithoutSlots?.length && !searchQuery) {
      <p-divider></p-divider>
      <div class="mt-2">
        <h3 class="text-base font-semibold text-orange-600 mb-2">
          <i class="pi pi-exclamation-circle mr-2"></i>
          Affaires du vivier sans créneau ({{
            detail.poolAffairsWithoutSlots!.length
          }})
        </h3>
        <div class="no-slot-grid">
          @for (item of detail.poolAffairsWithoutSlots!; track item.affairId) {
            <div class="no-slot-item">
              <div class="font-semibold text-sm">{{ item.affairName }}</div>
              <div class="text-xs text-color-secondary">
                {{ item.postalCode }} {{ item.locality }}
                @if (item.source) {
                  · {{ item.source }}
                }
              </div>
              <div class="text-xs font-mono text-color-secondary">
                {{ item.latitude | number: '1.5-5' }},
                {{ item.longitude | number: '1.5-5' }}
              </div>
              @if (item.affairProducts?.length) {
                <div class="affair-products mt-1">
                  @for (ap of item.affairProducts!; track $index) {
                    <span class="product-chip">
                      {{
                        ap['product_name'] ??
                          ap['product']?.['abbreviation'] ??
                          ap['product']?.['name'] ??
                          'Produit #' + ap['product_id']
                      }}
                    </span>
                  }
                </div>
              }
            </div>
          }
        </div>
      </div>
    }
  </div>
}
